import { useContext } from 'react';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import type { RbvcFeatureId } from './features';

/**
 * Returns whether the current user is allowed to see the given RBVC
 * feature surface.
 *
 *   - `true` if the rbvc_enabled flag is off (no restriction)
 *   - `true` if the server returned `rbvcAllowedFeatureIds === null`
 *     (admin / realm-admin / flag-off short-circuit on the backend)
 *   - `true` if userDetails has not yet loaded — matches the persona
 *     filter's "default-show during load" stance to avoid a flicker on
 *     first paint where granted features briefly appear hidden
 *   - otherwise `true` iff the id is present in the allow-list
 *
 * Closed-default semantics: a member in zero RBVC roles ends up with
 * `rbvcAllowedFeatureIds === []`, which hides every gated surface.
 */
export function useRbvcFeatureVisible(featureId: RbvcFeatureId): boolean {
  const { userInfo } = useContext(UserInfoContext);
  const enabled = !!userInfo?.features?.rbvcEnabled;
  if (!enabled) return true;

  // Loading: don't hide yet (avoids flicker; admin and rbvc-off both
  // resolve to null which we already short-circuit below).
  if (!userInfo) return true;

  const allowedIds = userInfo.rbvcAllowedFeatureIds;
  // null = admin / flag-off / errored = no restriction.
  if (allowedIds === null || allowedIds === undefined) return true;

  return allowedIds.includes(featureId);
}
